import React, { useEffect, useState } from "react";
import ItemCard from "../../components/ItemCard/ItemCard";
import supabase from "../../supabase";
import "./PropertyList.css";
import { useNotification } from "../../context/NotificationContext";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import { useUserPlan } from "../../context/UserPlanContext";
import { useAuth } from "../../context/AuthContext";
import { useRenovCredits } from "../../context/RenovCreditsContext";
import axios from "axios";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const PropertyList = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showNotification } = useNotification();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const { userPlan } = useUserPlan();
  const isPro = userPlan === "Pro";
  const [viewMode, setViewMode] = useState("cards"); // 'cards' | 'list'
  const { user } = useAuth();
  const { renovCredits } = useRenovCredits();
  const [analysisReadyMap, setAnalysisReadyMap] = useState({});
  const [loadingPdfMap, setLoadingPdfMap] = useState({});

  // Load saved viewMode for Pro users
  useEffect(() => {
    if (isPro) {
      const saved = localStorage.getItem("propertyListViewMode");
      if (saved === "cards" || saved === "list") {
        setViewMode(saved);
      }
    }
  }, [isPro]);

  // Persist viewMode when Pro toggles
  useEffect(() => {
    if (isPro) {
      localStorage.setItem("propertyListViewMode", viewMode);
    }
  }, [isPro, viewMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.rpc("get_properties_for_list");

        if (error) throw error;

        const props = data || [];
        setProperties(props);

        if (props.length > 0) {
          const propertyIds = props.map((p) => p.id);
          const { data: rooms, error: roomsErr } = await supabase
            .from("rooms")
            .select("id, property_id")
            .in("property_id", propertyIds);
          if (roomsErr) throw roomsErr;

          const roomsByProperty = (rooms || []).reduce((acc, r) => {
            (acc[r.property_id] ||= []).push(r.id);
            return acc;
          }, {});
          const allRoomIds = (rooms || []).map((r) => r.id);

          let analyzedSet = new Set();
          if (allRoomIds.length > 0) {
            const { data: analyses, error: analysesErr } = await supabase
              .from("room_analyses")
              .select("room_id")
              .in("room_id", allRoomIds);
            if (analysesErr) throw analysesErr;
            analyzedSet = new Set((analyses || []).map((a) => a.room_id));
          }

          const map = {};
          for (const pid of propertyIds) {
            const roomIds = roomsByProperty[pid] || [];
            map[pid] = roomIds.length > 0 && roomIds.every((rid) => analyzedSet.has(rid));
          }
          setAnalysisReadyMap(map);
        } else {
          setAnalysisReadyMap({});
        }
      } catch (error) {
        console.error("Erreur lors du chargement des propriétés :", error);
        showNotification("Impossible de charger les propriétés.", "danger");
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [showNotification]);

  const canGenerateFor = (propertyId) => {
    if (!isPro) return false;
    const allAnalyzed = !!analysisReadyMap[propertyId];
    const hasCredits = (renovCredits || 0) >= 20;
    return allAnalyzed && hasCredits;
  };

  const synthesisDisabledReason = (propertyId) => {
    if (!isPro) return "Réservé aux comptes Pro";
    if (!analysisReadyMap[propertyId]) {
      return "Toutes les pièces n'ont pas encore une analyse";
    }
    const diff = Math.max(0, 20 - (renovCredits || 0));
    if (diff > 0) {
      return `RC insuffisants: il manque ${diff} RC (coût 20 RC)`;
    }
    return null;
  };

  const synthesisButtonTitle = (propertyId) => {
    if (loadingPdfMap[propertyId]) return "Génération du PDF en cours…";
    const reason = synthesisDisabledReason(propertyId);
    return reason ? `Synthèse (PDF) — 20 RC • ${reason}` : "Synthèse (PDF) — 20 RC";
  };

  const handleGenerateSynthesis = async (propertyId) => {
    if (!user?.id) {
      showNotification("Utilisateur non connecté.", "danger");
      return;
    }
    setLoadingPdfMap((prev) => ({ ...prev, [propertyId]: true }));
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/openai/generate-property-synthesis-pdf`,
        { propertyId, userId: user.id },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "synthese-renovestim.pdf");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (e?.response?.status === 402) {
        showNotification(
          "Crédits insuffisants. Rendez-vous dans Mon Compte pour recharger vos RC.",
          "warning"
        );
      } else if (e?.response?.status === 400) {
        showNotification(
          "Toutes les pièces doivent être analysées pour générer la synthèse.",
          "warning"
        );
      } else if (e?.response?.status === 403) {
        showNotification("Réservé aux comptes Pro.", "warning");
      } else {
        console.error(e);
        showNotification("Erreur lors de la génération de la synthèse.", "danger");
      }
    } finally {
      setLoadingPdfMap((prev) => ({ ...prev, [propertyId]: false }));
    }
  };

  const handleDeleteProperty = (propertyId) => {
    setPropertyToDelete(propertyId);
    setShowDeleteModal(true);
  };

  const confirmDeleteProperty = async () => {
    if (!propertyToDelete) return;

    try {
      await supabase.from("images").delete().eq("property_id", propertyToDelete);
      await supabase.from("rooms").delete().eq("property_id", propertyToDelete);
      await supabase.from("properties").delete().eq("id", propertyToDelete);

      setProperties((prev) => prev.filter((p) => p.id !== propertyToDelete));

      showNotification("Propriété supprimée avec succès.", "success");
    } catch (error) {
      console.error("Erreur lors de la suppression de la propriété :", error);
      showNotification(
        "Une erreur s'est produite lors de la suppression de la propriété.",
        "danger"
      );
    } finally {
      setShowDeleteModal(false);
      setPropertyToDelete(null);
    }
  };

  return (
    <div className="container mt-4 property-list-container">
      <ConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteProperty}
        title="Confirmer la suppression"
        body="Êtes-vous sûr de vouloir supprimer cette propriété et toutes les pièces qu'elle contient ? Cette action est irréversible."
      />

      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0">Liste des Propriétés</h2>
        {isPro && (
          <div className="view-toggle" role="group" aria-label="Changer la vue">
            <button
              type="button"
              className={`btn btn-sm btn-light me-2 ${viewMode === "cards" ? "active" : ""}`}
              onClick={() => setViewMode("cards")}
              title="Vue cartes"
              aria-pressed={viewMode === "cards"}
            >
              <i className="bi bi-grid" aria-hidden="true"></i>
              <span className="visually-hidden">Cartes</span>
            </button>
            <button
              type="button"
              className={`btn btn-sm btn-light ${viewMode === "list" ? "active" : ""}`}
              onClick={() => setViewMode("list")}
              title="Vue liste"
              aria-pressed={viewMode === "list"}
            >
              <i className="bi bi-list" aria-hidden="true"></i>
              <span className="visually-hidden">Liste</span>
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <>
          {(viewMode === "cards" || !isPro) && (
            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 gx-4 gy-4">
              {properties.map((property) => (
                <div key={property.id} className="col">
                  <ItemCard
                    title={property.name}
                    text={`Superficie: ${property.superficie} m\u00B2`}
                    link={`/property/${property.id}`}
                    linkText="Voir les détails"
                    images={property.default_image_url ? [property.default_image_url] : []}
                    onDelete={() => handleDeleteProperty(property.id)}
                    extraActions={
                      isPro && (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>{synthesisButtonTitle(property.id)}</Tooltip>}
                        >
                          <span className="d-inline-block">
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm ms-2 btn-action-circle"
                              onClick={() => handleGenerateSynthesis(property.id)}
                              disabled={!canGenerateFor(property.id) || !!loadingPdfMap[property.id]}
                              style={(!canGenerateFor(property.id) || !!loadingPdfMap[property.id]) ? { pointerEvents: 'none' } : undefined}
                            >
                              {loadingPdfMap[property.id] ? (
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                              ) : (
                                <i className="bi bi-file-earmark-pdf"></i>
                              )}
                            </button>
                          </span>
                        </OverlayTrigger>
                      )
                    }
                  />
                </div>
              ))}

              <div className="col">
                <ItemCard
                  title="Ajouter une Propriété"
                  text="Cliquez pour ajouter une nouvelle propriété"
                  images={[]}
                  link="/add-property"
                  linkText="Ajouter"
                />
              </div>
            </div>
          )}

          {isPro && viewMode === "list" && (
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th scope="col">Propriété</th>
                    <th scope="col">Superficie</th>
                    <th scope="col" className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {properties.map((property) => (
                    <tr key={property.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          {property.default_image_url && (
                            <img
                              src={property.default_image_url}
                              alt=""
                              className="rounded me-3 property-thumb"
                            />
                          )}
                          <div>
                            <div className="fw-semibold">{property.name}</div>
                            <a href={`/property/${property.id}`} className="small text-decoration-none">
                              Voir les détails
                            </a>
                          </div>
                        </div>
                      </td>
                      <td>{property.superficie} m²</td>
                      <td className="text-end">
                        <div className="actions-group">
                          <a
                            href={`/property/${property.id}`}
                            className="btn btn-sm btn-outline-primary btn-icon-square"
                            title="Ouvrir"
                          >
                            <i className="bi bi-box-arrow-up-right" aria-hidden="true"></i>
                            <span className="visually-hidden">Ouvrir</span>
                          </a>
                          {isPro && (
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>{synthesisButtonTitle(property.id)}</Tooltip>}
                            >
                              <span className="btn-icon-wrapper">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary btn-icon-square"
                                  onClick={() => handleGenerateSynthesis(property.id)}
                                  disabled={!canGenerateFor(property.id) || !!loadingPdfMap[property.id]}
                                  style={(!canGenerateFor(property.id) || !!loadingPdfMap[property.id]) ? { pointerEvents: 'none' } : undefined}
                                >
                                  {loadingPdfMap[property.id] ? (
                                    <span
                                      className="spinner-border spinner-border-sm"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                  ) : (
                                    <i className="bi bi-file-earmark-pdf"></i>
                                  )}
                                </button>
                              </span>
                            </OverlayTrigger>
                          )}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger btn-icon-square"
                            onClick={() => handleDeleteProperty(property.id)}
                            title="Supprimer"
                          >
                            <i className="bi bi-trash" aria-hidden="true"></i>
                            <span className="visually-hidden">Supprimer</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan="3" className="text-center">
                      <a href="/add-property" className="btn btn-primary">
                        <i className="bi bi-plus-lg me-2" aria-hidden="true"></i>
                        Ajouter une Propriété
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PropertyList;
